import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Resets scroll to the top on route (pathname) change — React Router otherwise
 * preserves the previous page's scroll position. In-page anchor navigation
 * (a `#hash` in the URL) is left untouched so jump links still work.
 *
 * It runs before paint and jumps instantly, both on purpose. As a passive effect
 * it ran a frame late, and `html { scroll-behavior: smooth }` then animated it,
 * so the new page was first drawn at the old page's scroll offset. Arriving on a
 * shorter page from a scrolled one, that offset is the footer: it flashed into
 * view and slid away, which Chrome scored as a 0.56 layout shift on the
 * homepage form -> /chat path.
 */
function ScrollReset() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);

  return null;
}

/**
 * The layout effect lives in a child mounted only on the client: React 18 warns
 * about useLayoutEffect in every server render, and this component is in the
 * layout of all the prerendered pages.
 */
export function ScrollToTop() {
  const [mounted, setMounted] = useState(false);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- client-only mount flag, so the layout effect never reaches the SSG render
  useEffect(() => setMounted(true), []);
  return mounted ? <ScrollReset /> : null;
}
